/**
 * YearBoundaryLines (#2501) — the single emitter that turns a chart's visible
 * categories into the vertical year-boundary markers, so all three app
 * time-series charts (preisspiegel / liquiditaetsplanung / ernteprognose) stay
 * consistent instead of each re-deriving the boundary logic.
 *
 * Style: the SAME tokens as the existing "Heute" ``<ReferenceLine>`` in all three
 * charts — dotted ``2 3``, muted grey ``#9ca3af`` stroke, ``#6b7280`` label at
 * ``fontSize: 11`` — so the marker reads as the same family of background guide.
 * The label alone differs in placement: "Heute" owns ``insideTopRight``, so the
 * year label goes ``insideBottomLeft``. That way a year boundary landing next to
 * (or exactly on) "Heute" never obscures it — the labels sit at opposite corners,
 * and callers render these lines BEFORE their "Heute" line so recharts' source
 * z-order keeps "Heute" on top.
 *
 * Note for anyone moving one of these: on a VERTICAL ``<ReferenceLine>`` recharts
 * builds a zero-width label viewBox, so the corner names read backwards —
 * ``insideTopRight`` anchors the text ``end`` just LEFT of the line, and
 * ``insideTopLeft`` anchors it ``start`` just RIGHT of it. #2540 relies on
 * exactly that: the preisspiegel today-price labels sit right of the "Heute"
 * line, and "Heute" stays at ``insideTopRight`` — i.e. on the other side —
 * which is what keeps the two from ever overlapping.
 *
 * Emits a plain ``ReactElement[]`` rather than a wrapper component on purpose:
 * recharts discovers its children by element type, so a custom component
 * returning a fragment would not be picked up — whereas an array child is
 * flattened and detected (the same way each chart already maps its ``<Line>``s).
 */
import type { ReactElement } from 'react';
import { ReferenceLine } from 'recharts';
import { yearBoundariesInDomain, yearBoundaryMarkers } from './yearBoundaries';

/** "Heute"-matching stroke colour. */
export const YEAR_BOUNDARY_STROKE = '#9ca3af';
/** "Heute"-matching dotted dash pattern. */
export const YEAR_BOUNDARY_STROKE_DASHARRAY = '2 3';
/** "Heute"-matching label colour. */
export const YEAR_BOUNDARY_LABEL_FILL = '#6b7280';
/** "Heute"-matching label size. */
export const YEAR_BOUNDARY_LABEL_FONT_SIZE = 11;
/**
 * Bottom corner, opposite "Heute"'s ``insideTopRight``, so the two labels cannot
 * overlap when the lines are near each other.
 */
export const YEAR_BOUNDARY_LABEL_POSITION = 'insideBottomLeft';

/**
 * One ``<ReferenceLine>`` per year boundary inside the visible window.
 *
 * @param categories the chart's currently visible x-axis categories, ascending
 *   (i.e. the zoom window's rows) — boundaries outside the window yield nothing.
 *
 * Spread into a recharts chart's children ABOVE the "Heute" line:
 * ``{yearBoundaryLines(visibleRows.map((r) => r.date))}``.
 */
export function yearBoundaryLines(categories: readonly string[]): ReactElement[] {
  return yearBoundaryMarkers(categories).map((marker) => (
    <ReferenceLine
      key={`year-boundary-${marker.x}`}
      x={marker.x}
      stroke={YEAR_BOUNDARY_STROKE}
      strokeDasharray={YEAR_BOUNDARY_STROKE_DASHARRAY}
      label={{
        value: marker.label,
        position: YEAR_BOUNDARY_LABEL_POSITION,
        fontSize: YEAR_BOUNDARY_LABEL_FONT_SIZE,
        fill: YEAR_BOUNDARY_LABEL_FILL,
      }}
    />
  ));
}

/**
 * The same markers for a NUMERIC (epoch-ms) x-axis — #2543.
 *
 * The categorical emitter above has to SNAP each 01.01 onto the first visible
 * category at-or-after it, because recharts silently drops a ``<ReferenceLine>``
 * whose ``x`` is not an actual category. A time axis places the line at the real
 * ``Date.UTC(year, 0, 1)`` instead, so no snapping layer, no injected anchor row,
 * and the marker sits at the true calendar position even when the nearest
 * observation is months away.
 *
 * Boundary selection is the SAME pure ``yearBoundariesInDomain`` rule the
 * categorical path uses (via the boundary years of the two endpoints), so both
 * emitters agree on which turnovers are worth drawing: a boundary flush against
 * the domain START is skipped — nothing earlier is on-plot to separate it from —
 * while one at the END is kept, since there the turnover itself is visible.
 *
 * @param loMs domain start, epoch ms.
 * @param hiMs domain end, epoch ms.
 *
 * Spread into a chart's children ABOVE the "Heute" line:
 * ``{yearBoundaryLinesAt(zoom.domain.lo, zoom.domain.hi)}``.
 */
export function yearBoundaryLinesAt(loMs: number, hiMs: number): ReactElement[] {
  if (!Number.isFinite(loMs) || !Number.isFinite(hiMs) || hiMs <= loMs) return [];
  const loYear = new Date(loMs).getUTCFullYear();
  const hiYear = new Date(hiMs).getUTCFullYear();

  return yearBoundariesInDomain(String(loYear), String(hiYear)).map((boundary) => (
    <ReferenceLine
      key={`year-boundary-at-${boundary.year}`}
      x={Date.UTC(boundary.year, 0, 1)}
      stroke={YEAR_BOUNDARY_STROKE}
      strokeDasharray={YEAR_BOUNDARY_STROKE_DASHARRAY}
      label={{
        value: boundary.label,
        position: YEAR_BOUNDARY_LABEL_POSITION,
        fontSize: YEAR_BOUNDARY_LABEL_FONT_SIZE,
        fill: YEAR_BOUNDARY_LABEL_FILL,
      }}
    />
  ));
}
